import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { apiFetch, setToken, clearToken, getToken } from "@/lib/api";

const AuthContext = createContext(undefined);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [guest, setGuest] = useState(false);

  useEffect(() => {
    (async () => {
      const token = getToken();
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        const res = await apiFetch("/auth/refresh", { method: "POST" });
        setToken(res.token);
        setUser(res.user);
      } catch {
        clearToken();
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await apiFetch("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
    setToken(res.token);
    setGuest(false);
    setUser(res.user);
    return res.user;
  }, []);

  const signup = useCallback(async (payload) => {
    const res = await apiFetch("/auth/signup", {
      method: "POST",
      body: JSON.stringify(payload),
    });
    setToken(res.token);
    setGuest(false);
    setUser(res.user);
    return res.user;
  }, []);

  const continueAsGuest = useCallback(() => {
    setUser(null);
    setGuest(true);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const me = await apiFetch("/auth/me");
      setUser(me);
      return me;
    } catch (err) {
      console.error("Auth refresh failed:", err);
    }
  }, []);

  const logout = useCallback(() => {
    clearToken();
    setGuest(false);
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, guest, login, signup, continueAsGuest, refresh, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
